import { REASONS, CREATIVE_GROUP, VALUES_GROUP, ARCHETYPES, DAILY_MATCHUPS } from "../data/gameData";

const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

export function computeDailyResult(answers) {
  const rounds = DAILY_MATCHUPS;
  let correctPredictions = 0;
  let majorityAligned = 0;
  let creativeReasons = 0;
  let valuesReasons = 0;
  let totalReasons = 0;
  const difficultyCounts = { easy: 0, difficult: 0, nearly: 0, irreconcilable: 0 };
  const factorCounts = {};
  const reasonCounts = {};
  let hardest = null;

  answers.forEach((ans) => {
    const round = rounds.find((r) => r.id === ans.roundId);
    if (!round) return;
    if (ans.majorityPredictionId === round.majorityId) correctPredictions++;
    if (ans.selectedCelebrityId === round.majorityId) majorityAligned++;
    (ans.reasons || []).forEach((r) => {
      totalReasons++;
      reasonCounts[r] = (reasonCounts[r] || 0) + 1;
      if (CREATIVE_GROUP.has(r)) creativeReasons++;
      if (VALUES_GROUP.has(r)) valuesReasons++;
    });
    if (ans.decidingFactor) factorCounts[ans.decidingFactor] = (factorCounts[ans.decidingFactor] || 0) + 1;
    if (ans.difficulty) difficultyCounts[ans.difficulty] = (difficultyCounts[ans.difficulty] || 0) + 1;
    if (ans.difficulty === "nearly" || ans.difficulty === "irreconcilable") hardest = round.id;
  });

  const predictionAccuracy = pct(correctPredictions, rounds.length);
  const majorityAlignment = pct(majorityAligned, rounds.length);
  const contrarianScore = 100 - majorityAlignment;
  const creativePreference = pct(creativeReasons, totalReasons);
  const valuesPreference = pct(valuesReasons, totalReasons);

  // Top trait
  const topReasonId = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const topReasonLabel = REASONS.find((r) => r.id === topReasonId)?.label || "—";
  const topFactor = Object.entries(factorCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  // Archetype selection - deterministic ordering by strongest signal
  let archetypeId = "instinctive_picker";
  if (predictionAccuracy >= 80) archetypeId = "crowd_whisperer";
  else if (contrarianScore >= 60 && creativePreference >= 30) archetypeId = "creative_contrarian";
  else if (valuesPreference >= 40) archetypeId = "thoughtful_idealist";
  else if (majorityAlignment >= 60) archetypeId = "cultural_insider";
  else archetypeId = "instinctive_picker";

  const archetype = ARCHETYPES[archetypeId];

  return {
    predictionAccuracy,
    majorityAlignment,
    contrarianScore,
    creativePreference,
    valuesPreference,
    difficultyCounts,
    topReasonLabel,
    topReasonId,
    topFactor,
    hardestRoundId: hardest,
    archetype,
    completedAt: new Date().toISOString(),
  };
}

// Custom game comparison scoring
export function computeCustomComparison(game, participantAnswers) {
  const rounds = game.rounds;
  let choiceMatches = 0;
  let correctChoicePredictions = 0;
  let correctReasonPredictions = 0;
  let diffMatches = 0;
  let reasonOverlapTotal = 0;
  let reasonOverlapMax = 0;
  const unexpected = [];
  let mostRevealing = null;
  let biggestDiffRound = null;
  let sharedTopReason = null;
  const sharedReasonCounts = {};

  rounds.forEach((round, idx) => {
    const pAns = participantAnswers.find((a) => a.roundId === round.id);
    if (!pAns) return;

    // Prediction accuracy
    if (round.organizerPredictionId === pAns.selectedCelebrityId) correctChoicePredictions++;
    else unexpected.push(round.id);
    if (round.organizerPredictedReason && round.organizerPredictedReason === pAns.decidingFactor)
      correctReasonPredictions++;

    // Choice alignment vs organizer's own choice for that round
    if (round.organizerChoiceId && round.organizerChoiceId === pAns.selectedCelebrityId) choiceMatches++;

    // Reason overlap
    const organizerReasons = new Set(round.organizerReasons || []);
    const participantReasons = new Set(pAns.reasons || []);
    const overlap = [...organizerReasons].filter((r) => participantReasons.has(r)).length;
    const maxR = Math.max(organizerReasons.size, participantReasons.size, 1);
    reasonOverlapTotal += overlap;
    reasonOverlapMax += maxR;

    (pAns.reasons || []).forEach((r) => {
      if (organizerReasons.has(r)) sharedReasonCounts[r] = (sharedReasonCounts[r] || 0) + 1;
    });

    // Difficulty alignment
    if (round.organizerDifficulty && round.organizerDifficulty === pAns.difficulty) diffMatches++;

    if (!mostRevealing && overlap >= 1) mostRevealing = round.id;
    if (round.organizerChoiceId && round.organizerChoiceId !== pAns.selectedCelebrityId && !biggestDiffRound)
      biggestDiffRound = round.id;
  });

  const totalRounds = rounds.length || 1;
  const predictionAccuracy = Math.round(((correctChoicePredictions * 70 + correctReasonPredictions * 30) / (totalRounds * 100)) * 100);
  const choiceAlignment = Math.round(((choiceMatches * 100 + (totalRounds - choiceMatches) * 30) / (totalRounds * 100)) * 100);
  const reasonAlignment = Math.round((reasonOverlapTotal / (reasonOverlapMax || 1)) * 100);
  const difficultyAlignment = Math.round((diffMatches / totalRounds) * 100);

  const connectionScore = Math.round(
    reasonAlignment * 0.35 + predictionAccuracy * 0.3 + choiceAlignment * 0.2 + difficultyAlignment * 0.15
  );

  const topSharedId = Object.entries(sharedReasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  sharedTopReason = REASONS.find((r) => r.id === topSharedId)?.label || "—";

  return {
    predictionAccuracy,
    choiceAlignment,
    reasonAlignment,
    difficultyAlignment,
    connectionScore,
    correctChoicePredictions,
    correctReasonPredictions,
    unexpectedCount: unexpected.length,
    sharedTopReason,
    mostRevealingRoundId: mostRevealing || rounds[0]?.id,
    biggestDifferenceRoundId: biggestDiffRound || rounds[rounds.length - 1]?.id,
    totalRounds,
  };
}
